import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import Card from 'app/frontend/base/components/Card'
import { imageUrl } from 'app/frontend/base/helpers'
import {
  DevEncounterCreature,
  proxyDevEncounterCreatureImageUrl,
  saveDevEncounterCreatureImage,
  updateDevEncounterCreature,
} from 'app/frontend/base/api/devEncounterCreatures'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Eye, EyeOff } from 'lucide-react'
import {
  VIEWPORT_SIZE,
  coverFitTransform,
  exportCreaturePng,
  sampleBackgroundColor,
} from './creatureImageExport'

const MIN_ZOOM_FACTOR = 0.5
const MAX_ZOOM_FACTOR = 4

type CreatureCircleEditorProps = {
  encounterId: number
  creature: DevEncounterCreature
  saved: boolean
  onSaved: () => void
  onDisabledChange: (disabled: boolean) => void
}

const CreatureCircleEditor = ({
  encounterId,
  creature,
  saved,
  onSaved,
  onDisabledChange,
}: CreatureCircleEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const scaleRef = useRef(1)
  const offsetRef = useRef({ x: 0, y: 0 })
  const zoomBoundsRef = useRef({ min: 0.05, max: 8 })
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    originOffsetX: number
    originOffsetY: number
  } | null>(null)

  const [scale, setScale] = useState(1)
  const [fitScale, setFitScale] = useState(1)
  const [offsetX, setOffsetX] = useState(0)
  const [offsetY, setOffsetY] = useState(0)
  const [backgroundColor, setBackgroundColor] = useState('rgb(0, 0, 0)')
  const [imageLoaded, setImageLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [togglingDisabled, setTogglingDisabled] = useState(false)
  const [previewBust, setPreviewBust] = useState(0)

  const minScale = fitScale * MIN_ZOOM_FACTOR
  const maxScale = fitScale * MAX_ZOOM_FACTOR

  scaleRef.current = scale
  offsetRef.current = { x: offsetX, y: offsetY }
  zoomBoundsRef.current = { min: minScale, max: maxScale }

  const applyScale = (nextScale: number, originX: number, originY: number) => {
    const currentScale = scaleRef.current
    const { min, max } = zoomBoundsRef.current
    const clamped = Math.min(max, Math.max(min, nextScale))
    const ratio = clamped / currentScale
    setScale(clamped)
    setOffsetX(originX - (originX - offsetRef.current.x) * ratio)
    setOffsetY(originY - (originY - offsetRef.current.y) * ratio)
  }

  useEffect(() => {
    setScale(1)
    setFitScale(1)
    setOffsetX(0)
    setOffsetY(0)
    setBackgroundColor('rgb(0, 0, 0)')
    setImageLoaded(false)

    if (!creature.zoomUrl) return

    const image = new Image()
    image.onload = () => {
      imageRef.current = image
      const transform = coverFitTransform(image)
      setFitScale(transform.scale)
      setScale(transform.scale)
      setOffsetX(transform.offsetX)
      setOffsetY(transform.offsetY)
      setBackgroundColor(sampleBackgroundColor(image))
      setImageLoaded(true)
    }
    image.onerror = () => {
      toast.error(`Failed to load image for ${creature.name}`)
    }
    image.src = proxyDevEncounterCreatureImageUrl(creature.zoomUrl)
  }, [creature.id, creature.name, creature.zoomUrl])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const onWheelNative = (event: WheelEvent) => {
      if (!imageRef.current) return
      event.preventDefault()

      const rect = container.getBoundingClientRect()
      const cursorX = event.clientX - rect.left
      const cursorY = event.clientY - rect.top
      const zoomFactor = event.deltaY < 0 ? 1.08 : 1 / 1.08
      applyScale(scaleRef.current * zoomFactor, cursorX, cursorY)
    }

    container.addEventListener('wheel', onWheelNative, { passive: false })
    return () => container.removeEventListener('wheel', onWheelNative)
  }, [])

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!imageLoaded) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originOffsetX: offsetX,
      originOffsetY: offsetY,
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    setOffsetX(drag.originOffsetX + (event.clientX - drag.startX))
    setOffsetY(drag.originOffsetY + (event.clientY - drag.startY))
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null
    }
  }

  const handleToggleDisabled = async () => {
    setTogglingDisabled(true)
    try {
      const nextDisabled = !creature.disabled
      await updateDevEncounterCreature(creature.id, { disabled: nextDisabled })
      onDisabledChange(nextDisabled)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to update ${creature.name}`,
      )
    } finally {
      setTogglingDisabled(false)
    }
  }

  const handleSave = async () => {
    const image = imageRef.current
    if (!image || !imageLoaded) return

    setSaving(true)
    try {
      const blob = await exportCreaturePng(image, {
        scale,
        offsetX,
        offsetY,
        backgroundColor,
      })
      await saveDevEncounterCreatureImage(encounterId, creature.id, blob)
      setPreviewBust(Date.now())
      onSaved()
      toast.success(`Saved ${creature.name}`)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to save ${creature.name}`,
      )
    } finally {
      setSaving(false)
    }
  }

  const previewUrl = saved
    ? `${imageUrl(`encounter-creatures/${encounterId}/${creature.id}.png`)}?t=${
        previewBust || '1'
      }`
    : undefined

  return (
    <Card className='flex flex-col gap-3 h-full'>
      <div className='flex items-center justify-between gap-3'>
        <div className='text-lg font-semibold'>{creature.name}</div>
        <div className='flex items-center gap-2'>
          <Button
            size='icon'
            variant='outline'
            onClick={handleToggleDisabled}
            disabled={togglingDisabled}
            title={creature.disabled ? 'Enable creature' : 'Disable creature'}
          >
            {creature.disabled ? <EyeOff size={16} /> : <Eye size={16} />}
          </Button>
          <Button
            size='sm'
            onClick={handleSave}
            disabled={!imageLoaded || saving || !creature.zoomUrl}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <div className='flex flex-wrap items-start gap-6'>
        <div className='flex flex-col gap-2 items-center'>
          <div className='text-sm text-muted-foreground'>Editor</div>
          <div
            ref={containerRef}
            className='relative overflow-hidden rounded-full border border-border cursor-grab active:cursor-grabbing touch-none'
            style={{
              width: VIEWPORT_SIZE,
              height: VIEWPORT_SIZE,
              backgroundColor,
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {imageLoaded && imageRef.current ? (
              <img
                src={imageRef.current.src}
                alt={creature.name}
                draggable={false}
                className='absolute max-w-none pointer-events-none select-none'
                style={{
                  width: imageRef.current.naturalWidth * scale,
                  height: imageRef.current.naturalHeight * scale,
                  left: offsetX,
                  top: offsetY,
                }}
              />
            ) : (
              <div className='flex h-full w-full items-center justify-center text-sm text-muted-foreground'>
                {creature.zoomUrl ? 'Loading…' : 'No zoom URL'}
              </div>
            )}
          </div>
          <Slider
            className='w-[200px]'
            min={minScale}
            max={maxScale}
            step={(maxScale - minScale) / 200}
            value={[scale]}
            disabled={!imageLoaded}
            onValueChange={([value]) => {
              applyScale(value, VIEWPORT_SIZE / 2, VIEWPORT_SIZE / 2)
            }}
          />
        </div>

        {previewUrl ? (
          <div className='flex flex-col gap-2 items-center'>
            <div className='text-sm text-muted-foreground'>Saved</div>
            <img
              src={previewUrl}
              alt={`${creature.name} saved`}
              className='rounded-full border border-border'
              style={{
                width: VIEWPORT_SIZE,
                height: VIEWPORT_SIZE,
                backgroundColor,
              }}
            />
          </div>
        ) : null}
      </div>
    </Card>
  )
}

export default CreatureCircleEditor
